import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { DownloadIcon } from "@chakra-ui/icons";
import {
	Button,
	Card,
	CardBody,
	CardHeader,
	Flex,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Stack,
	Text,
	useDisclosure,
	useToast,
} from "@chakra-ui/react";
import moment from "moment";

import FinancialStatusTable from "../../../Components/FinancialStatus/FinancialStatusTable";
import FinancialStatusWarnings from "../../../Components/FinancialStatus/FinancialStatusWarnings";
import { buildYearBreakdown } from "../../../utils/financialStatus";
import { API } from "../../../utils/http";

export default function FinancialStatus() {
	const owner = useSelector((state) => state.user.user);
	// Raw array keeps the breakdown memo stable across local re-renders.
	const storeProperties = useSelector((state) => state.user?.user?.properties);
	const properties = useMemo(() => (storeProperties || []).filter((p) => !p.archived), [storeProperties]);
	const { t } = useTranslation();
	const [year, setYear] = useState(moment().year());
	const { isOpen: isExportModalOpen, onOpen: onExportModalOpen, onClose: onExportModalClose } = useDisclosure();
	const toast = useToast();

	const financialStatus = owner?.financialStatus;
	const ownerCurrency = owner?.bankDetails?.currency;

	const yearBreakdown = useMemo(
		() =>
			buildYearBreakdown({
				properties,
				year,
				ownerCurrency,
				financialStatus,
			}),
		[properties, year, ownerCurrency, financialStatus],
	);

	const handleExportFinancialStatus = async (format = "pdf") => {
		try {
			const response = await API.put("export-financial-status", {
				ownerEmail: owner?.email,
				outputFormat: format,
			});
			if (!response.data || !response.data.output) {
				toast({
					title: "Error",
					description: "Failed to export financial status",
					status: "error",
				});
				return;
			}
			const mimeType = format === "pdf" ? "application/pdf" : "text/csv";
			const link = document.createElement("a");
			link.href = `data:${mimeType};base64,${response.data.output}`;
			link.download = `financial-status-${owner?.name}-${moment().format("DD-MM-YYYY")}.${format}`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (error) {
			toast({
				title: "Error",
				description: error.response?.data?.error?.message || "Failed to export financial status",
				status: "error",
				duration: 5000,
				isClosable: true,
			});
		}
	};

	const renderExportModal = () => (
		<Modal isOpen={isExportModalOpen} onClose={onExportModalClose} size="md">
			<ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
			<ModalContent>
				<ModalHeader>
					<Text>Export Financial Status</Text>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Stack spacing={4}>
						<Stack direction="row" spacing={4}>
							<Button
								leftIcon={<DownloadIcon />}
								colorScheme="blue"
								onClick={() => {
									handleExportFinancialStatus("pdf");
									onExportModalClose();
								}}
								flex={1}
							>
								Export PDF
							</Button>
							<Button
								leftIcon={<DownloadIcon />}
								colorScheme="blue"
								onClick={() => {
									handleExportFinancialStatus("csv");
									onExportModalClose();
								}}
								flex={1}
							>
								Export CSV
							</Button>
						</Stack>
					</Stack>
				</ModalBody>
				<ModalFooter>
					<Button colorScheme="red" onClick={onExportModalClose}>
						Cancel
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);

	return (
		<Card overflowX={{ sm: "scroll", xl: "hidden" }}>
			<CardHeader>
				<Flex justifyContent="space-between" alignItems="center">
					<Text fontSize="xl" fontWeight="bold">
						{t("navbarMenu.financialStatus")}
					</Text>
					<Button colorScheme="blue" leftIcon={<DownloadIcon />} onClick={onExportModalOpen}>
						{t("financialStatus.exportFinancialStatus")}
					</Button>
				</Flex>
			</CardHeader>
			<CardBody>
				<Stack gap={3}>
					{renderExportModal()}
					<FinancialStatusWarnings warnings={yearBreakdown.warnings} />
					<Flex direction="row" alignItems="center" justifyContent="center" gap={4}>
						<Button size="sm" onClick={() => setYear(year - 1)}>
							Prev year
						</Button>
						<Text fontWeight="bold" fontSize="lg" minW="4rem" textAlign="center">
							{year}
						</Text>
						<Button size="sm" isDisabled={year === moment().year()} onClick={() => setYear(year + 1)}>
							Next year
						</Button>
					</Flex>
					<FinancialStatusTable
						breakdown={yearBreakdown}
						year={year}
						ownerCurrency={ownerCurrency}
						financialStatus={financialStatus}
					/>
				</Stack>
			</CardBody>
		</Card>
	);
}
